/**
 * Pure chat list merging logic — no React or Zustand dependencies.
 * Used by the unified "Tutte" view of the messages drawer and by the tests.
 */

import type { MetaConversation } from '../stores/metaMessagingStore';
import { isHiddenSystemChat } from '../services/whatsapp/chatListRules';

export interface WhatsAppChatLike {
  id: string;
  name?: string;
  isGroup?: boolean;
  profilePicUrl?: string;
  lastMessage?: { timestamp: number; body?: string; fromMe?: boolean }; // Unix seconds from WAHA
  unreadCount?: number;
}

/** Chat di team gia' normalizzata dal chiamante (nome visibile, ultimo messaggio). */
export interface TeamChatLike {
  id: string;
  name: string;
  avatarUrl?: string | null;
  isGroup?: boolean;
  snippet?: string;
  timestamp: number; // milliseconds since epoch
  unreadCount?: number;
  raw?: unknown;
}

export type UnifiedChannel = 'whatsapp' | 'instagram' | 'messenger' | 'team';

export interface UnifiedChatItem {
  id: string;
  source: 'whatsapp' | 'meta' | 'team';
  /** Canale visibile (etichetta di provenienza nella lista "Tutte"). */
  channel: UnifiedChannel;
  name: string;
  snippet: string;
  /** Non letti (WhatsApp, Team) o 1 se in attesa di risposta (Meta). */
  unreadCount: number;
  avatarUrl: string | null;
  isGroup: boolean;
  timestamp: number; // milliseconds since epoch
  // Org di appartenenza della conversazione: serve all'etichetta lato superadmin, che può
  // avere righe di org diverse. `null` per WhatsApp, dove la sessione WAHA non la espone.
  organizationId: string | null;
  raw: unknown;
}

function metaNeedsReply(c: MetaConversation): boolean {
  if (!c.last_inbound_at) return false;
  if (!c.last_message_at) return true;
  return new Date(c.last_inbound_at).getTime() >= new Date(c.last_message_at).getTime();
}

export function mergeChatLists(
  whatsappChats: WhatsAppChatLike[],
  metaConversations: MetaConversation[],
  teamChats: TeamChatLike[] = [],
  metaNames?: Map<string, string>,
  metaUnreadIds?: Set<string>
): UnifiedChatItem[] {
  const waItems: UnifiedChatItem[] = whatsappChats.filter((c) => !isHiddenSystemChat(String(c.id ?? ''))).map((c) => ({
    id: c.id,
    source: 'whatsapp',
    channel: 'whatsapp',
    name: c.name || String(c.id ?? '').split('@')[0],
    snippet: c.lastMessage?.body ?? '',
    unreadCount: c.unreadCount ?? 0,
    avatarUrl: c.profilePicUrl ?? null,
    isGroup: Boolean(c.isGroup),
    // WhatsApp timestamp is Unix seconds (see src/components/whatsapp2/sidebar/ChatListItem.tsx: "* 1000")
    // Convert to milliseconds for consistent sorting
    timestamp: c.lastMessage?.timestamp ? c.lastMessage.timestamp * 1000 : 0,
    organizationId: null,
    raw: c,
  }));

  const metaItems: UnifiedChatItem[] = metaConversations.map((c) => {
    const tail = (c.external_contact_id ?? '').slice(-6);
    const fallback = c.channel === 'instagram' ? `Contatto Instagram ${tail}` : `Contatto Messenger ${tail}`;
    const pending = metaNeedsReply(c);
    // Con le letture per utente (meta_conversation_reads) "non letto" e' esplicito;
    // senza (test, primo caricamento) vale "in attesa di risposta".
    const unread = metaUnreadIds ? metaUnreadIds.has(c.id) : pending;
    return {
      id: c.id,
      source: 'meta',
      channel: c.channel,
      name: metaNames?.get(c.id) ?? c.contact_name ?? (c.profile_username ? `@${c.profile_username}` : fallback),
      snippet: c.last_message_preview
        ? `${c.last_message_direction === 'outbound' ? 'Tu: ' : ''}${c.last_message_preview}`
        : pending ? 'In attesa di risposta' : '',
      unreadCount: unread ? 1 : 0,
      avatarUrl: c.profile_pic_url ?? null,
      isGroup: false,
      // Meta last_message_at is ISO 8601 string; convert to milliseconds via getTime()
      timestamp: c.last_message_at ? new Date(c.last_message_at).getTime() : 0,
      organizationId: c.organization_id ?? null,
      raw: c,
    };
  });

  const teamItems: UnifiedChatItem[] = teamChats.map((c) => ({
    id: c.id,
    source: 'team',
    channel: 'team',
    name: c.name,
    snippet: c.snippet ?? '',
    unreadCount: c.unreadCount ?? 0,
    avatarUrl: c.avatarUrl ?? null,
    isGroup: Boolean(c.isGroup),
    timestamp: c.timestamp,
    organizationId: null,
    raw: c.raw ?? c,
  }));

  // Sort by timestamp descending (newest first)
  return [...waItems, ...metaItems, ...teamItems].sort((a, b) => b.timestamp - a.timestamp);
}

export type UnreadCounts = { all: number; whatsapp: number; instagram: number; messenger: number; team: number };

/**
 * Contatori delle schede: conversazioni da leggere per canale, SENZA le chat
 * silenziate (stessa regola del numero sul menu, isMuted di chatListRules).
 * Chiave delle preferenze: chat_jid per WhatsApp, 'meta:<id>' per Instagram/Messenger;
 * le chat di team non hanno il silenzio.
 */
export function countUnreadByChannel(
  items: UnifiedChatItem[],
  isMutedKey: (key: string) => boolean,
): UnreadCounts {
  const c: UnreadCounts = { all: 0, whatsapp: 0, instagram: 0, messenger: 0, team: 0 };
  for (const item of items) {
    if (item.unreadCount <= 0) continue;
    const key = item.source === 'whatsapp' ? item.id : item.source === 'meta' ? `meta:${item.id}` : null;
    if (key && isMutedKey(key)) continue;
    c[item.channel] += 1;
    c.all += 1;
  }
  return c;
}
